import {Button, Title} from '@priority-software/priority-style-react';
import React, {useRef, useState} from 'react';
import {TreeDropdown} from '../parts/TreeDropdown/TreeDropdown';
import {useEmailContext} from '../../../../../context';
import {texts} from '../../../../../texts';
import styles from '../../../../styles.module.scss';
import {useEventCallback} from 'usehooks-ts';
import {TPersonalizeDropdownHandle, TPersonalizeStates} from './types';
import {initialStates} from '../../../constants';
import {Tags} from '../parts/Tags/Tags';
import {insertIdTreeContent} from '../../../../../utils/insertIdTreeContent';
import {IPersonalizeCmpProps} from '../Personalize';
import {useFocusTarget} from './useFocusTarget';
import {createDataTestId} from '../../../../../../../utils/dataTestId';

type TUsePersonalizeCmp = IPersonalizeCmpProps & {};

export const usePersonalizeCmp = ({
    messageCursorLastPosition,
    subjectInputRef,
    targetAutoFill
}: TUsePersonalizeCmp) => {
    const {
        states,
        handlers: {updatePersonalize, updateEmailSubject}
    } = useEmailContext();

    const {handleFocusTarget} = useFocusTarget({
        messageCursorLastPosition,
        subjectInputRef,
        targetAutoFill
    });

    const [personalizeStates, setPersonalizeStates] = useState<TPersonalizeStates>(initialStates);

    const refTreeDropdown = useRef<TPersonalizeDropdownHandle>(null);

    const resetValues = () => {
        refTreeDropdown.current?.resetTreeDropdown();
    };

    const handleClick = useEventCallback(() => {
        const tagValue = personalizeStates.tree
            ? `${personalizeStates.tree?.extraData?.formTitle}.${personalizeStates.tree?.title}`
            : '';
        const isFocusEditor = handleFocusTarget(updateEmailSubject, tagValue);
        if (isFocusEditor) {
            updatePersonalize(personalizeStates);
        }

        setPersonalizeStates(initialStates);
        resetValues();
    });

    const handleTagClick = async (tag: string) => {
        const isFocusEditor = handleFocusTarget(updateEmailSubject, tag);
        if (isFocusEditor) {
            await insertIdTreeContent(states.emailEditor, tag);
        }
    };

    const renderHeader = () => (
        <Title
            componentClasses={styles.fullWidth}
            skin="h4"
            {...createDataTestId(`email-dynamic-values-header-title`, true)}
        >
            {texts.add_dynamic_value_title}
        </Title>
    );

    const renderBody = () => (
        <React.Fragment>
            <Tags
                {...{onClick: handleTagClick, tags: [texts.rule_number, texts.rule_description]}}
            />
            <TreeDropdown {...{setPersonalizeStates, ref: refTreeDropdown}} />
        </React.Fragment>
    );

    const renderFooter = () => (
        <Button
            {...createDataTestId(`email-dynamic-values-footer-button`, true)}
            onClick={handleClick}
            disabled={!personalizeStates.tree}
            width="fullWidth"
            size={'large'}
        >
            {texts.add}
        </Button>
    );

    return {renderHeader, renderBody, renderFooter} as const;
};
